import { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';

import { useLoggedInUser } from 'src/services/api/auth/use-logged-in-user';
import { queryKeys } from 'src/services/api/constants';
import { fetchLead } from 'src/services/api/leads/use-lead';
import type { ILead } from 'src/services/api/leads/types';
import { usePollConversations } from 'src/services/api/messages/use-poll-conversations';
import { subscribeToFrames } from 'src/services/websocket/live-updates-signal';
import { useIsLive } from 'src/services/websocket/use-is-live';
import { useLiveRefetch } from 'src/services/websocket/use-live-refetch';
import { useWebSocket } from 'src/services/websocket/use-websocket';
import { isMessageFrame, parseLeadCreatedFrame } from 'src/services/websocket/ws-protocol';

export default function LiveUpdatesHost() {
    useWebSocket();
    const isLive = useIsLive();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = useLoggedInUser();
    const conversations = usePollConversations(loggedInUser, isLive);
    const previousIsLiveRef = useRef(isLive);

    useEffect(() => {
        const wasLive = previousIsLiveRef.current;
        previousIsLiveRef.current = isLive;
        if (isLive && !wasLive) {
            void queryClient.refetchQueries([queryKeys.leads], { exact: true });
        }
    }, [isLive, queryClient]);

    useEffect(() => subscribeToFrames((frame) => {
        if (isMessageFrame(frame)) {
            void queryClient.invalidateQueries(
                [queryKeys.messages, frame.lead_uuid],
                { refetchActive: false, refetchInactive: false },
            );
        }
    }), [queryClient]);

    useEffect(() => {
        const unsubscribe = subscribeToFrames((frame) => {
            const leadCreatedFrame = parseLeadCreatedFrame(frame);
            if (!leadCreatedFrame) {
                return;
            }

            void queryClient.fetchQuery<ILead>(
                [queryKeys.leads, leadCreatedFrame.lead_uuid],
                () => fetchLead(leadCreatedFrame.lead_uuid),
            )
                .then((lead) => {
                    queryClient.setQueryData<ILead[]>([queryKeys.leads], (cachedLeads = []) => (
                        [lead, ...cachedLeads.filter(({ uuid }) => uuid !== lead.uuid)]
                            .sort((a, b) => (
                                new Date(b.inserted_at).getTime() - new Date(a.inserted_at).getTime()
                            ))
                    ));
                })
                .catch(() => undefined);
        });

        return unsubscribe;
    }, [queryClient]);

    useLiveRefetch({
        isLive,
        enabled: conversations.enabled,
        refetch: conversations.refetch,
        shouldRefetchFrame: (frame) => frame.type === 'message.created',
    });

    return null;
}
